import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { capitalCase } from "change-case";
import type React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

export type EntityDataTableFieldFormatter = (
  value: unknown,
  fieldName: string,
) => React.ReactNode;

export type EntityDataTableProps = {
  /** Key-value data to display. Keys starting with _ are omitted. */
  data: Record<string, unknown>;
  /** Custom formatters per field name. When present, used instead of default formatting. */
  fieldFormatters?: Record<string, EntityDataTableFieldFormatter>;
  /** Function to format field labels (default: capitalCase). */
  formatFieldLabel?: (key: string) => string;
  /** Optional class name for the table wrapper. */
  className?: string;
  /** Optional style for the table wrapper. */
  style?: React.CSSProperties;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === "[object Object]"
  );
}

function isArrayOfObjects(value: unknown): value is Record<string, unknown>[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every((v) => isRecord(v));
}

function getAllKeys(rows: Record<string, unknown>[]): string[] {
  const set = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!key.startsWith("_")) set.add(key);
    }
  }
  return Array.from(set);
}

function isIdLikeField(fieldName: string): boolean {
  return /id/i.test(fieldName);
}

function formatFieldValue(
  value: unknown,
  fieldName: string,
  fieldFormatters: Record<string, EntityDataTableFieldFormatter> | undefined,
  formatFieldLabel: (key: string) => string,
  depth: number,
): React.ReactNode {
  const custom = fieldFormatters?.[fieldName];
  if (custom) {
    const rendered = custom(value, fieldName);
    if (rendered !== undefined && rendered !== null) return rendered;
  }

  if (value === null || value === undefined) {
    return <span className="text-muted-foreground italic">-</span>;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "number") {
    if (fieldName === "unix_timestamp" || fieldName === "timestamp") {
      return String(value);
    }
    return value.toLocaleString();
  }

  if (typeof value === "string") {
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </a>
      );
    }
    if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return (
            <span title={value}>
              {date.toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          );
        }
      } catch {
        // fall through
      }
    }
    if (isIdLikeField(fieldName)) {
      return (
        <span className="block min-w-0 truncate" title={value}>
          {value}
        </span>
      );
    }
    return <span className="break-words">{value}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-muted-foreground italic">Empty array</span>;
    }
    if (
      value.every(
        (v) =>
          typeof v === "string" ||
          typeof v === "number" ||
          typeof v === "boolean",
      )
    ) {
      return (
        <span className="break-words">{value.map(String).join(", ")}</span>
      );
    }
    if (isArrayOfObjects(value)) {
      const keys = getAllKeys(value);
      return (
        <div className="overflow-x-auto max-w-full">
          <Table>
            <TableHeader>
              <TableRow>
                {keys.map((k) => (
                  <TableHead key={k} className="whitespace-nowrap p-3">
                    {formatFieldLabel(k)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {value.map((row, idx) => (
                <TableRow key={idx}>
                  {keys.map((k) => (
                    <TableCell key={k} className="align-top p-3">
                      {isIdLikeField(k) ? (
                        <div
                          className="min-w-0 truncate"
                          title={
                            typeof row[k] === "string"
                              ? (row[k] as string)
                              : undefined
                          }
                        >
                          {formatFieldValue(
                            row[k],
                            k,
                            fieldFormatters,
                            formatFieldLabel,
                            depth + 1,
                          )}
                        </div>
                      ) : (
                        formatFieldValue(
                          row[k],
                          k,
                          fieldFormatters,
                          formatFieldLabel,
                          depth + 1,
                        )
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
    }
    return (
      <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-32">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  if (isRecord(value)) {
    const entries = Object.entries(value).filter(
      ([key]) => !key.startsWith("_"),
    );
    if (entries.length === 0) {
      return <span className="text-muted-foreground italic">Empty object</span>;
    }
    return (
      <div className="overflow-x-auto max-w-full">
        <Table>
          <VisuallyHidden>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px] whitespace-nowrap">
                  Field
                </TableHead>
                <TableHead>Value</TableHead>
              </TableRow>
            </TableHeader>
          </VisuallyHidden>
          <TableBody>
            {entries.map(([k, v]) => (
              <TableRow key={k}>
                <TableCell className="font-medium text-muted-foreground align-top p-3">
                  {formatFieldLabel(k)}
                </TableCell>
                <TableCell className="align-top p-3">
                  {isIdLikeField(k) ? (
                    <div
                      className="min-w-0 truncate"
                      title={typeof v === "string" ? v : undefined}
                    >
                      {formatFieldValue(
                        v,
                        k,
                        fieldFormatters,
                        formatFieldLabel,
                        depth + 1,
                      )}
                    </div>
                  ) : (
                    formatFieldValue(
                      v,
                      k,
                      fieldFormatters,
                      formatFieldLabel,
                      depth + 1,
                    )
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return String(value);
}

export function EntityDataTable({
  data,
  fieldFormatters,
  formatFieldLabel = (key) => capitalCase(key).replace(/id/gi, "ID"),
  className,
  style,
}: EntityDataTableProps) {
  const entries = Object.entries(data).filter(([key]) => !key.startsWith("_"));

  return (
    <Table className={className} style={style}>
      <VisuallyHidden>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px]">Field</TableHead>
            <TableHead>Value</TableHead>
          </TableRow>
        </TableHeader>
      </VisuallyHidden>
      <TableBody>
        {entries.map(([key, value]) => (
          <TableRow key={key}>
            <TableCell className="font-medium text-muted-foreground align-top w-[25%]">
              {formatFieldLabel(key)}
            </TableCell>
            <TableCell
              className={isIdLikeField(key) ? "max-w-0 w-px" : undefined}
            >
              {isIdLikeField(key) ? (
                <div
                  className="min-w-0 truncate"
                  title={typeof value === "string" ? value : undefined}
                >
                  {formatFieldValue(
                    value,
                    key,
                    fieldFormatters,
                    formatFieldLabel,
                    0,
                  )}
                </div>
              ) : (
                formatFieldValue(
                  value,
                  key,
                  fieldFormatters,
                  formatFieldLabel,
                  0,
                )
              )}
            </TableCell>
          </TableRow>
        ))}
        {entries.length === 0 && (
          <TableRow>
            <TableCell
              colSpan={2}
              className="text-center text-muted-foreground"
            >
              No data available
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
